// Writer memory client.
//
// Memory is a list of short, human-readable notes the agent writes while it
// works. The author reads them, corrects the odd one, and deletes what is
// wrong — they never fill in a form. The only structured settings left are
// the two that are genuinely cheap to state: tone and banned words.

import { supabase } from '@/integrations/supabase/client';

export type MemoryNoteKind = 'preference' | 'decision' | 'source' | 'context';

export const MEMORY_NOTE_KINDS: MemoryNoteKind[] = [
  'preference',
  'decision',
  'source',
  'context',
];

/** Plain-language headings, in the order the panel shows them. */
export const MEMORY_KIND_LABELS: Record<MemoryNoteKind, string> = {
  preference: 'How you write',
  decision: 'What we decided',
  source: 'Sources and why they matter',
  context: 'About this work',
};

export const MAX_NOTE_CHARS = 300;

export interface MemoryNote {
  id: string;
  kind: MemoryNoteKind;
  note: string;
  citekey: string | null;
  origin: 'agent' | 'author';
  created_at: string;
  updated_at: string;
}

export interface WriterUserPreferences {
  tone: string | null;
  banned_words: string[];
}

/** Parse a comma / newline separated list into trimmed, unique, non-empty items. */
export function parseCommaList(raw: string): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const chunk of raw.split(/[,\n]/)) {
    const t = chunk.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Render a list back into a comma-separated string suitable for an input. */
export function formatCommaList(items: string[] | null | undefined): string {
  if (!items || items.length === 0) return '';
  return items.join(', ');
}

/** Group notes for display, preserving the canonical kind order. */
export function groupNotesByKind(
  notes: MemoryNote[],
): Array<{ kind: MemoryNoteKind; label: string; notes: MemoryNote[] }> {
  return MEMORY_NOTE_KINDS.map((kind) => ({
    kind,
    label: MEMORY_KIND_LABELS[kind],
    notes: notes.filter((n) => n.kind === kind),
  })).filter((group) => group.notes.length > 0);
}

function normaliseKind(value: unknown): MemoryNoteKind {
  return MEMORY_NOTE_KINDS.includes(value as MemoryNoteKind)
    ? (value as MemoryNoteKind)
    : 'context';
}

export async function loadMemoryNotes(projectId: string): Promise<MemoryNote[]> {
  const { data, error } = await supabase
    .from('writer_memory_notes')
    .select('id, kind, note, citekey, origin, created_at, updated_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
    id: String(row.id),
    kind: normaliseKind(row.kind),
    note: String(row.note ?? ''),
    citekey: (row.citekey as string | null) ?? null,
    origin: row.origin === 'author' ? 'author' : 'agent',
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  }));
}

export async function addMemoryNote(
  projectId: string,
  kind: MemoryNoteKind,
  note: string,
): Promise<MemoryNote> {
  const text = note.replace(/\s+/g, ' ').trim().slice(0, MAX_NOTE_CHARS);
  if (!text) throw new Error('Write a short note first');
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('writer_memory_notes')
    .insert({
      project_id: projectId,
      owner_id: uid,
      kind,
      note: text,
      origin: 'author',
    })
    .select('id, kind, note, citekey, origin, created_at, updated_at')
    .single();
  if (error) throw new Error(error.message);
  const row = data as Record<string, unknown>;
  return {
    id: String(row.id),
    kind: normaliseKind(row.kind),
    note: String(row.note ?? ''),
    citekey: (row.citekey as string | null) ?? null,
    origin: 'author',
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

export async function updateMemoryNote(id: string, note: string): Promise<void> {
  const text = note.replace(/\s+/g, ' ').trim().slice(0, MAX_NOTE_CHARS);
  if (!text) throw new Error('A note cannot be empty');
  const { error } = await supabase
    .from('writer_memory_notes')
    .update({ note: text, origin: 'author' })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteMemoryNote(id: string): Promise<void> {
  const { error } = await supabase.from('writer_memory_notes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function clearMemoryNotes(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('writer_memory_notes')
    .delete()
    .eq('project_id', projectId);
  if (error) throw new Error(error.message);
}

export async function loadUserPreferences(): Promise<WriterUserPreferences | null> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) return null;
  const { data, error } = await supabase
    .from('writer_user_preferences')
    .select('tone, banned_words')
    .eq('owner_id', uid)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return {
    tone: (data as { tone: string | null }).tone ?? null,
    banned_words: Array.isArray((data as { banned_words: unknown }).banned_words)
      ? ((data as { banned_words: string[] }).banned_words)
      : [],
  };
}

export async function saveUserPreferences(
  patch: WriterUserPreferences,
): Promise<void> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) throw new Error('Not authenticated');
  const { error } = await supabase
    .from('writer_user_preferences')
    .upsert(
      { owner_id: uid, tone: patch.tone, banned_words: patch.banned_words },
      { onConflict: 'owner_id' },
    );
  if (error) throw new Error(error.message);
}
